import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AiUsageBreakdownRow,
  AiUsageEvent,
  AiUsageFacets,
  AiUsagePage,
  AiUsageSummary,
  ApiError,
  api,
} from "../lib/api";
import { useAuth } from "../lib/auth";

interface Filters {
  q: string;
  organization_id: number | null;
  case_id: number | null;
  user_id: number | null;
  feature: string;
  model: string;
  provider: string;
  success: "" | "true" | "false";
  date_from: string;
  date_to: string;
  limit: number;
  offset: number;
}

const DEFAULT_FILTERS: Filters = {
  q: "",
  organization_id: null,
  case_id: null,
  user_id: null,
  feature: "",
  model: "",
  provider: "",
  success: "",
  date_from: "",
  date_to: "",
  limit: 50,
  offset: 0,
};

// Coarse feature buckets the backend stamps each row with. Kept here so
// the dropdown label matches the chip color in the table. Plain ASCII
// separators so we never depend on the source file's encoding to render
// a clean label in the SPA.
const FEATURE_BUCKETS: { value: string; label: string; chip: string }[] = [
  { value: "",                  label: "Any feature",                chip: "other" },
  { value: "chat_qa",           label: "Chat - Q&A",                 chip: "query" },
  { value: "chat_insights",     label: "Chat - Insights",            chip: "query" },
  { value: "timeline_extract",  label: "Document ingest",            chip: "document" },
  { value: "doc_translate",     label: "Document ingest - translation", chip: "document" },
  { value: "doc_image_vision",  label: "Document ingest - image captioning", chip: "document" },
  { value: "video_vision",      label: "Video - scene captioning",   chip: "video" },
  { value: "video_transcribe",  label: "Video - transcription",      chip: "video" },
  { value: "video_chat",        label: "Video - in-player chat",     chip: "video" },
  { value: "demo_gen",          label: "Admin - demo generator",     chip: "user" },
];

function chipFor(feature: string): string {
  return FEATURE_BUCKETS.find((b) => b.value === feature)?.chip ?? "other";
}

function formatUsd(value: number | null | undefined, fractionDigits = 4): string {
  if (value == null) return "-";
  return `$${value.toFixed(fractionDigits)}`;
}

function formatInt(value: number | null | undefined): string {
  if (value == null) return "-";
  return value.toLocaleString();
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export default function AdminAiUsage() {
  const user = useAuth((s) => s.user);
  const isOwner = user?.role === "owner";
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState<AiUsagePage | null>(null);
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [facets, setFacets] = useState<AiUsageFacets | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<AiUsageEvent | null>(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const filters: Filters = useMemo(() => readFiltersFromUrl(searchParams), [searchParams]);
  const [draftQ, setDraftQ] = useState(filters.q);
  useEffect(() => { setDraftQ(filters.q); }, [filters.q]);

  useEffect(() => {
    if (!selected && !breakdownOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (selected) setSelected(null);
      else if (breakdownOpen) setBreakdownOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected, breakdownOpen]);

  // Facets refresh whenever the org / date range changes - they're cheap
  // and we want the dropdowns to mirror the active scope. Non-owners are
  // short-circuited so the API isn't hit just to be 403'd; the in-page
  // restriction message below handles the UX.
  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (filters.organization_id != null) params.set("organization_id", String(filters.organization_id));
        if (filters.date_from) params.set("date_from", filters.date_from);
        if (filters.date_to) params.set("date_to", filters.date_to);
        const f = await api.get<AiUsageFacets>(`/admin/ai-usage/facets?${params.toString()}`);
        if (!cancelled) setFacets(f);
      } catch (e: unknown) {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : "Failed to load filters");
      }
    })();
    return () => { cancelled = true; };
  }, [isOwner, filters.organization_id, filters.date_from, filters.date_to]);

  // Page + summary load together so the kpi tiles + table always agree.
  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      setErr(null);
      try {
        const qs = buildQueryString(filters);
        const sumQs = buildSummaryQueryString(filters);
        const [p, s] = await Promise.all([
          api.get<AiUsagePage>(`/admin/ai-usage?${qs}`),
          api.get<AiUsageSummary>(`/admin/ai-usage/summary?${sumQs}`),
        ]);
        if (!cancelled) {
          setPage(p);
          setSummary(s);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setPage(null);
          setSummary(null);
          setErr(e instanceof ApiError ? e.message : "Failed to load AI usage");
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOwner, filters]);

  function update(patch: Partial<Filters>) {
    const next: Filters = { ...filters, ...patch };
    if (!("offset" in patch)) next.offset = 0;
    setSearchParams(filtersToUrl(next), { replace: true });
  }

  function resetFilters() {
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    update({ q: draftQ });
  }

  if (user && !isOwner) {
    return (
      <div className="activity">
        <header className="activity__head">
          <div>
            <div className="page-eyebrow">AI usage</div>
            <h1>AI usage</h1>
          </div>
          <Link to="/" className="btn">Back to cases</Link>
        </header>
        <div className="alert alert--error" role="alert">
          AI usage is restricted to organization owners.
        </div>
      </div>
    );
  }

  const total = page?.total ?? 0;
  const pageStart = total === 0 ? 0 : filters.offset + 1;
  const pageEnd = Math.min(filters.offset + filters.limit, total);
  const hasPrev = filters.offset > 0;
  const hasNext = filters.offset + filters.limit < total;

  // The filtered grand-total comes from the list endpoint (which sums the
  // entire filtered set on the server, ignoring pagination) so it stays
  // accurate even while the user is paging.
  const filteredCost = page?.total_cost_usd ?? null;
  const breakdownAvailable = !!summary && (
    (summary.by_feature.length > 0) ||
    (summary.by_model.length > 0) ||
    (summary.by_organization.length > 0) ||
    (summary.by_case.length > 0) ||
    (summary.by_user.length > 0)
  );

  return (
    <div className="activity ai-usage">
      <header className="activity__head">
        <div>
          <div className="page-eyebrow">Site analytics</div>
          <h1>AI usage</h1>
          <p className="activity__lead">
            System-wide view of every outbound AI call made by the platform -
            chat questions, document timeline extraction, video scene
            captioning, and transcription - with model, tokens, and cost
            across all organizations. Use the Organization filter to narrow
            to a single tenant.
          </p>
        </div>
        <Link to="/" className="btn">Back to cases</Link>
      </header>

      {err && <div className="alert alert--error">{err}</div>}

      <SummaryCards summary={summary} busy={busy} />

      <form className="activity__filters" onSubmit={onSubmit}>
        <div className="activity__filter activity__filter--wide">
          <label htmlFor="aiu-q">Search</label>
          <input
            id="aiu-q"
            type="search"
            placeholder="Model, feature, user, case, org, target..."
            value={draftQ}
            onChange={(e) => setDraftQ(e.target.value)}
          />
        </div>
        <div className="activity__filter">
          <label htmlFor="aiu-org">Organization</label>
          <select
            id="aiu-org"
            value={filters.organization_id ?? ""}
            onChange={(e) => update({ organization_id: e.target.value ? Number(e.target.value) : null, case_id: null })}
          >
            <option value="">Any organization</option>
            {(facets?.organizations ?? [])
              .filter((o) => o.organization_id != null)
              .map((o) => (
                <option key={String(o.organization_id)} value={String(o.organization_id)}>
                  {o.name ?? `Org #${o.organization_id}`} ({o.count.toLocaleString()})
                </option>
              ))}
          </select>
        </div>
        <div className="activity__filter">
          <label htmlFor="aiu-case">Case</label>
          <select
            id="aiu-case"
            value={filters.case_id ?? ""}
            onChange={(e) => update({ case_id: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Any case</option>
            {(facets?.cases ?? [])
              .filter((c) => c.case_id != null)
              .filter((c) =>
                filters.organization_id == null ||
                c.organization_id === filters.organization_id,
              )
              .map((c) => (
                <option key={String(c.case_id)} value={String(c.case_id)}>
                  {c.case_name ?? `Case #${c.case_id}`} ({c.count.toLocaleString()})
                </option>
              ))}
          </select>
        </div>
        <div className="activity__filter">
          <label htmlFor="aiu-user">User</label>
          <select
            id="aiu-user"
            value={filters.user_id ?? ""}
            onChange={(e) => update({ user_id: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Anyone</option>
            {(facets?.users ?? [])
              .filter((u) => u.user_id != null)
              .map((u) => (
                <option key={String(u.user_id)} value={String(u.user_id)}>
                  {u.display_name ?? u.email ?? `User #${u.user_id}`} ({u.count.toLocaleString()})
                </option>
              ))}
          </select>
        </div>
        <div className="activity__filter">
          <label htmlFor="aiu-feature">Feature</label>
          <select
            id="aiu-feature"
            value={filters.feature}
            onChange={(e) => update({ feature: e.target.value })}
          >
            {FEATURE_BUCKETS.map((b) => {
              const hit = facets?.features.find((f) => f.feature === b.value);
              return (
                <option key={b.value} value={b.value}>
                  {b.label}{hit ? ` (${hit.count.toLocaleString()})` : ""}
                </option>
              );
            })}
            {(facets?.features ?? [])
              .filter((f) => !FEATURE_BUCKETS.some((b) => b.value === f.feature))
              .map((f) => (
                <option key={f.feature} value={f.feature}>
                  {f.feature_label} ({f.count.toLocaleString()})
                </option>
              ))}
          </select>
        </div>
        <div className="activity__filter">
          <label htmlFor="aiu-model">Model</label>
          <select
            id="aiu-model"
            value={filters.model}
            onChange={(e) => update({ model: e.target.value })}
          >
            <option value="">Any model</option>
            {(facets?.models ?? []).map((m) => (
              <option key={`${m.provider}/${m.model}`} value={m.model}>
                {m.model} ({m.count.toLocaleString()})
              </option>
            ))}
          </select>
        </div>
        <div className="activity__filter">
          <label htmlFor="aiu-success">Outcome</label>
          <select
            id="aiu-success"
            value={filters.success}
            onChange={(e) => update({ success: e.target.value as Filters["success"] })}
          >
            <option value="">Any outcome</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </select>
        </div>
        <div className="activity__filter">
          <label htmlFor="aiu-from">From</label>
          <input
            id="aiu-from"
            type="date"
            value={filters.date_from}
            onChange={(e) => update({ date_from: e.target.value })}
          />
        </div>
        <div className="activity__filter">
          <label htmlFor="aiu-to">To</label>
          <input
            id="aiu-to"
            type="date"
            value={filters.date_to}
            onChange={(e) => update({ date_to: e.target.value })}
          />
        </div>
        <div className="activity__filter">
          <label htmlFor="aiu-limit">Page size</label>
          <select
            id="aiu-limit"
            value={filters.limit}
            onChange={(e) => update({ limit: Number(e.target.value) })}
          >
            {[25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div className="activity__filter activity__filter--actions">
          <button type="submit" className="btn btn--primary">Search</button>
          <button type="button" className="btn" onClick={resetFilters}>Reset</button>
        </div>
      </form>

      <FilteredTotalBanner
        busy={busy}
        total={total}
        filteredCost={filteredCost}
        pageStart={pageStart}
        pageEnd={pageEnd}
        breakdownAvailable={breakdownAvailable}
        onOpenBreakdown={() => setBreakdownOpen(true)}
      />

      <div className="activity__scroll">
        <table className="activity__table">
          <thead>
            <tr>
              <th className="activity__col-time">When</th>
              <th>Feature</th>
              <th>Model</th>
              <th>User</th>
              <th>Case</th>
              <th>Tokens / Audio</th>
              <th>Cost</th>
            </tr>
          </thead>
          <tbody>
            {(page?.items ?? []).map((row) => (
              <tr
                key={row.id}
                className={`activity__row activity__row--${chipFor(row.feature)} activity__row--clickable`}
                tabIndex={0}
                role="button"
                aria-label={`Show details for ${row.feature_label}`}
                onClick={() => setSelected(row)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelected(row);
                  }
                }}
              >
                <td className="activity__col-time" title={row.ts}>
                  {formatTime(row.ts)}
                </td>
                <td>
                  <span className={`activity__chip activity__chip--${chipFor(row.feature)}`}>
                    {row.feature_label}
                  </span>
                  {!row.success && (
                    <div className="muted activity__sub" style={{ color: "var(--danger, #e74c3c)" }}>
                      Failed
                    </div>
                  )}
                </td>
                <td>
                  <div>{row.model}</div>
                  <div className="muted activity__sub">{row.provider}</div>
                </td>
                <td>
                  {row.user_display_name ? (
                    <>
                      <div>{row.user_display_name}</div>
                      {row.organization_name && (
                        <div className="muted activity__sub">{row.organization_name}</div>
                      )}
                    </>
                  ) : row.organization_name ? (
                    <span>{row.organization_name}</span>
                  ) : (
                    <span className="muted">-</span>
                  )}
                </td>
                <td>
                  {row.case_id ? (
                    <Link
                      to={`/cases/${row.case_id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {row.case_name ?? `Case #${row.case_id}`}
                    </Link>
                  ) : (
                    <span className="muted">-</span>
                  )}
                </td>
                <td>
                  {row.audio_seconds != null ? (
                    <span>{row.audio_seconds.toFixed(1)}s audio</span>
                  ) : (
                    <span>
                      {formatInt(row.input_tokens)} in / {formatInt(row.output_tokens)} out
                    </span>
                  )}
                </td>
                <td>
                  <strong>{formatUsd(row.cost_usd, 4)}</strong>
                  {row.cost_usd == null && (
                    <div className="muted activity__sub">no rate</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {page && page.items.length === 0 && !busy && (
          <div className="empty">
            No AI calls match these filters. Try widening the date range
            or clearing the filters.
          </div>
        )}
      </div>

      <div className="activity__pager">
        <button
          type="button"
          className="btn"
          disabled={!hasPrev || busy}
          onClick={() => update({ offset: Math.max(0, filters.offset - filters.limit) })}
        >
          &laquo; Previous
        </button>
        <span className="muted">
          Page {Math.floor(filters.offset / filters.limit) + 1}
          {total > 0 && ` of ${Math.max(1, Math.ceil(total / filters.limit))}`}
        </span>
        <button
          type="button"
          className="btn"
          disabled={!hasNext || busy}
          onClick={() => update({ offset: filters.offset + filters.limit })}
        >
          Next &raquo;
        </button>
      </div>

      {selected && (
        <DetailModal event={selected} onClose={() => setSelected(null)} />
      )}
      {breakdownOpen && summary && (
        <BreakdownModal
          summary={summary}
          filteredCost={filteredCost}
          totalCalls={total}
          onClose={() => setBreakdownOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary tiles + filtered-total banner + per-dimension breakdown modal
// ---------------------------------------------------------------------------

function SummaryCards({
  summary,
  busy,
}: {
  summary: AiUsageSummary | null;
  busy: boolean;
}) {
  if (!summary) {
    return (
      <div className="ai-usage__totals">
        <div className="ai-usage__tile ai-usage__tile--ghost">
          {busy ? "Loading totals..." : "No data yet."}
        </div>
      </div>
    );
  }
  const t = summary.totals;
  return (
    <div className="ai-usage__totals">
      <div className="ai-usage__tile">
        <div className="ai-usage__tile-label">Total spend</div>
        <div className="ai-usage__tile-value">{formatUsd(t.total_cost_usd, 4)}</div>
        <div className="muted ai-usage__tile-sub">
          across {t.total_calls.toLocaleString()} call{t.total_calls === 1 ? "" : "s"}
        </div>
      </div>
      <div className="ai-usage__tile">
        <div className="ai-usage__tile-label">Tokens</div>
        <div className="ai-usage__tile-value">{formatInt(t.total_input_tokens + t.total_output_tokens)}</div>
        <div className="muted ai-usage__tile-sub">
          {formatInt(t.total_input_tokens)} in / {formatInt(t.total_output_tokens)} out
        </div>
      </div>
      <div className="ai-usage__tile">
        <div className="ai-usage__tile-label">Audio transcribed</div>
        <div className="ai-usage__tile-value">
          {t.total_audio_seconds >= 60
            ? `${(t.total_audio_seconds / 60).toFixed(1)} min`
            : `${t.total_audio_seconds.toFixed(1)} s`}
        </div>
        <div className="muted ai-usage__tile-sub">whisper / transcribe</div>
      </div>
      <div className="ai-usage__tile">
        <div className="ai-usage__tile-label">Reliability</div>
        <div className="ai-usage__tile-value">
          {t.total_calls === 0
            ? "-"
            : `${((t.success_calls / t.total_calls) * 100).toFixed(1)}%`}
        </div>
        <div className="muted ai-usage__tile-sub">
          {t.failed_calls.toLocaleString()} failed
        </div>
      </div>
    </div>
  );
}

function FilteredTotalBanner({
  busy,
  total,
  filteredCost,
  pageStart,
  pageEnd,
  breakdownAvailable,
  onOpenBreakdown,
}: {
  busy: boolean;
  total: number;
  filteredCost: number | null;
  pageStart: number;
  pageEnd: number;
  breakdownAvailable: boolean;
  onOpenBreakdown: () => void;
}) {
  return (
    <div className="ai-usage__filtered">
      <div className="ai-usage__filtered-summary">
        <div className="ai-usage__filtered-label">Filtered total</div>
        <div className="ai-usage__filtered-value">
          {filteredCost == null ? "-" : formatUsd(filteredCost, 4)}
        </div>
        <div className="muted ai-usage__filtered-sub">
          {busy ? (
            "Loading..."
          ) : total === 0 ? (
            "No AI calls match these filters."
          ) : (
            <>
              {total.toLocaleString()} call{total === 1 ? "" : "s"} total
              {" - showing "}
              {pageStart.toLocaleString()}-{pageEnd.toLocaleString()}
            </>
          )}
        </div>
      </div>
      <div className="ai-usage__filtered-actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={!breakdownAvailable}
          onClick={onOpenBreakdown}
          title={breakdownAvailable ? "Break down the filtered total by feature, model, organization, case, or user" : "No data to break down yet"}
        >
          View breakdown
        </button>
      </div>
    </div>
  );
}

type BreakdownTabKey = "feature" | "model" | "organization" | "case" | "user";

interface BreakdownTab {
  key: BreakdownTabKey;
  title: string;
  rows: AiUsageBreakdownRow[];
}

function BreakdownModal({
  summary,
  filteredCost,
  totalCalls,
  onClose,
}: {
  summary: AiUsageSummary;
  filteredCost: number | null;
  totalCalls: number;
  onClose: () => void;
}) {
  const tabs: BreakdownTab[] = [
    { key: "feature",      title: "Feature",      rows: summary.by_feature },
    { key: "model",        title: "Model",        rows: summary.by_model },
    { key: "organization", title: "Organization", rows: summary.by_organization },
    { key: "case",         title: "Case",         rows: summary.by_case },
    { key: "user",         title: "User",         rows: summary.by_user },
  ];
  const firstWithRows = tabs.find((t) => t.rows.length > 0) ?? tabs[0];
  const [active, setActive] = useState<BreakdownTabKey>(firstWithRows.key);
  const tab = tabs.find((t) => t.key === active) ?? tabs[0];

  const grandCost = summary.totals.total_cost_usd || 0;
  const maxRowCost = tab.rows.reduce((m, r) => Math.max(m, r.cost_usd), 0);
  return (
    <div
      className="activity-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="activity-modal ai-usage__breakdown-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aiu-breakdown-title"
      >
        <header className="activity-modal__head">
          <div>
            <span className="page-eyebrow">Cost breakdown</span>
            <h2 id="aiu-breakdown-title" className="activity-modal__title">
              {filteredCost == null ? "-" : formatUsd(filteredCost, 4)}
              <span className="muted" style={{ fontWeight: 400, fontSize: "14px", marginLeft: 8 }}>
                across {totalCalls.toLocaleString()} call{totalCalls === 1 ? "" : "s"}
              </span>
            </h2>
            <div className="muted activity__sub">
              Totals reflect the current filters - not just the visible page.
            </div>
          </div>
          <button
            type="button"
            className="activity-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            &times;
          </button>
        </header>

        <div className="ai-usage__breakdown-tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={t.key === active}
              className={`ai-usage__breakdown-tab${t.key === active ? " is-active" : ""}`}
              disabled={t.rows.length === 0}
              onClick={() => setActive(t.key)}
            >
              {t.title}
              <span className="muted">{t.rows.length > 0 ? ` (${t.rows.length})` : ""}</span>
            </button>
          ))}
        </div>

        <div className="ai-usage__breakdown-scroll">
          {tab.rows.length === 0 ? (
            <div className="empty">No {tab.title.toLowerCase()} data for the current filters.</div>
          ) : (
            <div className="ai-usage__breakdown-list ai-usage__breakdown-list--modal">
              {tab.rows.map((r) => {
                const pctOfGrand = grandCost > 0 ? (r.cost_usd / grandCost) * 100 : 0;
                const widthPct = maxRowCost > 0 ? (r.cost_usd / maxRowCost) * 100 : 0;
                return (
                  <div key={`${tab.key}:${r.key}`} className="ai-usage__breakdown-row">
                    <div className="ai-usage__breakdown-row-head">
                      <span className="ai-usage__breakdown-row-label" title={r.label}>
                        {r.label || r.key || "-"}
                      </span>
                      <span className="ai-usage__breakdown-row-cost">
                        {formatUsd(r.cost_usd, 4)}
                      </span>
                    </div>
                    <div className="ai-usage__breakdown-bar">
                      <div
                        className="ai-usage__breakdown-bar-fill"
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                    <div className="muted ai-usage__breakdown-row-sub">
                      {r.call_count.toLocaleString()} call{r.call_count === 1 ? "" : "s"}
                      {grandCost > 0 && ` - ${pctOfGrand.toFixed(1)}% of total`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <footer className="activity-modal__foot">
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail modal
// ---------------------------------------------------------------------------

function DetailModal({
  event,
  onClose,
}: {
  event: AiUsageEvent;
  onClose: () => void;
}) {
  const chip = chipFor(event.feature);
  return (
    <div
      className="activity-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="activity-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aiu-title"
      >
        <header className="activity-modal__head">
          <div>
            <span className={`activity__chip activity__chip--${chip}`}>{event.feature_label}</span>
            <h2 id="aiu-title" className="activity-modal__title">
              {event.model} <span className="muted">({event.provider})</span>
            </h2>
            <div className="activity__event-id muted">{event.feature}{event.task_key ? ` - ${event.task_key}` : ""}</div>
          </div>
          <button
            type="button"
            className="activity-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            &times;
          </button>
        </header>

        <dl className="activity-modal__grid">
          <dt>When</dt>
          <dd>
            {formatTime(event.ts)}
            <div className="muted activity__sub">{event.ts}</div>
          </dd>

          <dt>Outcome</dt>
          <dd>
            {event.success ? (
              <span>Success</span>
            ) : (
              <span style={{ color: "var(--danger, #e74c3c)" }}>Failed</span>
            )}
            {event.duration_ms != null && (
              <div className="muted activity__sub">{event.duration_ms} ms wall time</div>
            )}
          </dd>

          <dt>Cost</dt>
          <dd>
            <strong>{formatUsd(event.cost_usd, 6)}</strong>
            {(event.input_per_1m_usd != null || event.output_per_1m_usd != null) && (
              <div className="muted activity__sub">
                {event.input_per_1m_usd != null && (
                  <>in ${event.input_per_1m_usd.toFixed(2)}/M</>
                )}
                {event.input_per_1m_usd != null && event.output_per_1m_usd != null && " / "}
                {event.output_per_1m_usd != null && (
                  <>out ${event.output_per_1m_usd.toFixed(2)}/M</>
                )}
              </div>
            )}
            {event.audio_per_minute_usd != null && (
              <div className="muted activity__sub">
                ${event.audio_per_minute_usd.toFixed(4)}/minute of audio
              </div>
            )}
          </dd>

          <dt>Usage</dt>
          <dd>
            {event.audio_seconds != null && (
              <div>{event.audio_seconds.toFixed(2)} s of audio</div>
            )}
            {event.input_tokens != null && (
              <div>{formatInt(event.input_tokens)} input tokens</div>
            )}
            {event.output_tokens != null && (
              <div>{formatInt(event.output_tokens)} output tokens</div>
            )}
          </dd>

          <dt>User</dt>
          <dd>
            {event.user_display_name || event.user_email ? (
              <>
                {event.user_display_name && <div>{event.user_display_name}</div>}
                {event.user_email && (
                  <div className="muted activity__sub">{event.user_email}</div>
                )}
                {event.user_id != null && (
                  <div className="muted activity__sub">User #{event.user_id}</div>
                )}
              </>
            ) : (
              <span className="muted">-</span>
            )}
          </dd>

          <dt>Organization</dt>
          <dd>
            {event.organization_name ? (
              <>{event.organization_name}</>
            ) : event.organization_id ? (
              <span className="muted">Org #{event.organization_id}</span>
            ) : (
              <span className="muted">-</span>
            )}
          </dd>

          <dt>Case</dt>
          <dd>
            {event.case_id ? (
              <Link to={`/cases/${event.case_id}`} onClick={onClose}>
                {event.case_name ?? `Case #${event.case_id}`}
              </Link>
            ) : (
              <span className="muted">-</span>
            )}
          </dd>

          <dt>Request</dt>
          <dd>
            {event.request_ref_type || event.request_ref_label ? (
              <>
                {event.request_ref_label && <div>{event.request_ref_label}</div>}
                {(event.request_ref_type || event.request_ref_id != null) && (
                  <div className="muted activity__sub">
                    {event.request_ref_type ?? "-"}
                    {event.request_ref_id != null ? ` #${event.request_ref_id}` : ""}
                  </div>
                )}
              </>
            ) : (
              <span className="muted">-</span>
            )}
          </dd>

          {event.error_message && (
            <>
              <dt>Error</dt>
              <dd
                className="activity-modal__mono activity-modal__wrap"
                style={{ color: "var(--danger, #e74c3c)" }}
              >
                {event.error_message}
              </dd>
            </>
          )}

          {event.metadata && Object.keys(event.metadata).length > 0 && (
            <>
              <dt>Metadata</dt>
              <dd>
                <pre className="activity-modal__json">
                  {JSON.stringify(event.metadata, null, 2)}
                </pre>
              </dd>
            </>
          )}
        </dl>

        <footer className="activity-modal__foot">
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// URL <-> filter state plumbing
// ---------------------------------------------------------------------------

function buildQueryString(f: Filters): string {
  const p = new URLSearchParams();
  if (f.q.trim()) p.set("q", f.q.trim());
  if (f.organization_id != null) p.set("organization_id", String(f.organization_id));
  if (f.case_id != null) p.set("case_id", String(f.case_id));
  if (f.user_id != null) p.set("user_id", String(f.user_id));
  if (f.feature) p.set("feature", f.feature);
  if (f.model) p.set("model", f.model);
  if (f.provider) p.set("provider", f.provider);
  if (f.success) p.set("success", f.success);
  if (f.date_from) p.set("date_from", f.date_from);
  if (f.date_to) p.set("date_to", f.date_to);
  p.set("limit", String(f.limit));
  p.set("offset", String(f.offset));
  return p.toString();
}

function buildSummaryQueryString(f: Filters): string {
  const p = new URLSearchParams();
  if (f.q.trim()) p.set("q", f.q.trim());
  if (f.organization_id != null) p.set("organization_id", String(f.organization_id));
  if (f.case_id != null) p.set("case_id", String(f.case_id));
  if (f.user_id != null) p.set("user_id", String(f.user_id));
  if (f.feature) p.set("feature", f.feature);
  if (f.model) p.set("model", f.model);
  if (f.provider) p.set("provider", f.provider);
  if (f.success) p.set("success", f.success);
  if (f.date_from) p.set("date_from", f.date_from);
  if (f.date_to) p.set("date_to", f.date_to);
  return p.toString();
}

function readFiltersFromUrl(p: URLSearchParams): Filters {
  const limit = clamp(Number(p.get("limit") ?? DEFAULT_FILTERS.limit) || DEFAULT_FILTERS.limit, 1, 200);
  const offset = Math.max(0, Number(p.get("offset") ?? 0) || 0);
  const successRaw = p.get("success") ?? "";
  const success: Filters["success"] =
    successRaw === "true" ? "true" : successRaw === "false" ? "false" : "";
  return {
    q: p.get("q") ?? "",
    organization_id: p.get("organization_id") ? Number(p.get("organization_id")) : null,
    case_id: p.get("case_id") ? Number(p.get("case_id")) : null,
    user_id: p.get("user_id") ? Number(p.get("user_id")) : null,
    feature: p.get("feature") ?? "",
    model: p.get("model") ?? "",
    provider: p.get("provider") ?? "",
    success,
    date_from: p.get("date_from") ?? "",
    date_to: p.get("date_to") ?? "",
    limit,
    offset,
  };
}

function filtersToUrl(f: Filters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.organization_id != null) p.set("organization_id", String(f.organization_id));
  if (f.case_id != null) p.set("case_id", String(f.case_id));
  if (f.user_id != null) p.set("user_id", String(f.user_id));
  if (f.feature) p.set("feature", f.feature);
  if (f.model) p.set("model", f.model);
  if (f.provider) p.set("provider", f.provider);
  if (f.success) p.set("success", f.success);
  if (f.date_from) p.set("date_from", f.date_from);
  if (f.date_to) p.set("date_to", f.date_to);
  if (f.limit !== DEFAULT_FILTERS.limit) p.set("limit", String(f.limit));
  if (f.offset > 0) p.set("offset", String(f.offset));
  return p;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}
